import { useEffect, useMemo, useRef } from 'react';
import throttle from 'lodash/throttle';

import { THROTTLE_ANIMATION } from './particles.consts';
import { ParticleContainer, ParticleContextValue } from './particles.types';
import { createHtmlID, initializeTSParticles } from './particles.utils';
import { getOptions } from './configs/getOptions';
import { getDefaultEmitter } from './configs/getDefaultEmitter';

// @INFO consider using user settings to possibly disable such effects
export const useParticles = () => {
  const containerIdRef = useRef(createHtmlID());
  const particlesContainer = useRef<ParticleContainer | null>(null);

  const contextValue: ParticleContextValue = useMemo(() => {
    const internalPlay = async (emitter = getDefaultEmitter()) => {
      if (!particlesContainer.current) {
        const defaultOptions = getOptions();
        particlesContainer.current = await initializeTSParticles(
          containerIdRef.current,
          defaultOptions
        );
      }

      const container = particlesContainer.current;
      container.addEmitter(emitter);
    };

    const throttledPlay = throttle(internalPlay, THROTTLE_ANIMATION, {
      leading: true,
      trailing: false,
    });

    return {
      play: throttledPlay,
    };
  }, []);

  useEffect(() => {
    const container = particlesContainer.current;
    if (!container) return;

    return () => container.destroy();
  }, []);

  return {
    contextValue,
    containerHtmlId: containerIdRef.current,
  };
};
